import { lazy, useState, Suspense } from 'react';
import {
	TextField,
	Button,
	LinearProgress,
	Link,
	Grid,
	Typography,
	Box,
	useTheme
} from '@mui/material';
import { authenticationPagesStyles } from 'constants/styles';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { validateEmailRegex, validateSixDigitCode } from 'utils/validateForm';
import ErrorMessage from 'utils/errorMessages';
import { objectHasKeys } from 'utils/helpers';
import { resetSnackbarAlerts } from 'reducers/globalSlice';
import AloftLogo from 'assets/images/aloft_logo_white.png';
import AirControl from 'assets/images/air_control_white.png';
import { amplifyConfirmSignUp, amplifyResendCode } from 'actions/authentication';
import ResendCodeEmailInput from 'components/authentication/ResendCodeEmailInput';
import queryString from 'query-string';
import { stringifyQueryObj } from 'utils/urlHelpers';
import { useTranslation } from 'react-i18next';

const AuthenticationFooter = lazy(() => import('components/authentication/AuthenticationFooter'));
const LanguageSwitcherWrap = lazy(() => import('components/elements/LanguageSwitcherWrap'));

const ConfirmSignUp = () => {
	const theme = useTheme();
	const styles = {
		...authenticationPagesStyles(theme)
	};
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [showLoader, setShowLoader] = useState(false);
	const [showResendCodeLoader, setShowResendCodeLoader] = useState(false);
	const [showResendCodeEmailInput, setShowResendCodeEmailInput] = useState(false);
	const [showResendCodeButton, setShowResendCodeButton] = useState(true);
	const queryParameters = queryString.parse(window.location.search);

	const {
		formState: { errors },
		control,
		handleSubmit,
		getValues
	} = useForm({
		defaultValues: {
			code: '',
			email: queryParameters.email
		}
	});

	const handleConfirmSignUp = async () => {
		dispatch(resetSnackbarAlerts([]));
		setShowLoader(true);
		const { email, code } = getValues();
		await dispatch(
			amplifyConfirmSignUp({ username: email, code, navigate, queryParams: queryParameters })
		);
		setShowLoader(false);
	};

	const hasErrors = objectHasKeys(errors);

	const handleClickLogin = () => {
		//Pass along any query params that were sent by the mobile app
		navigate({
			pathname: '/login',
			search: stringifyQueryObj(queryParameters)
		});
	};

	const handleResendCode = async () => {
		setShowResendCodeLoader(true);
		const { email } = getValues();
		await dispatch(amplifyResendCode(email));
		setShowResendCodeLoader(false);
	};

	const handleRequestResend = async () => {
		setShowResendCodeButton(false);
		const { email } = getValues();
		if (email) {
			await handleResendCode();
		} else {
			setShowResendCodeEmailInput(true);
		}
	};

	return (
		<Suspense fallback={null}>
			<LanguageSwitcherWrap>
				<Box sx={styles.main}>
					<Box sx={styles.headerContainer}>
						<Box
							component={'img'}
							src={AloftLogo}
							alt=''
							onClick={handleClickLogin}
							sx={styles.logo}
						/>
						<Link
							href='https://www.aloft.ai/support/'
							target='_blank'
							rel='noopener noreferrer'
							underline='none'
						>
							<Button
								sx={{ ...styles.outlineButton, ...styles.topSupportButton }}
								variant='outlined'
							>
								{t('Support')}
							</Button>
						</Link>
					</Box>
					<Grid container spacing={2} sx={styles.mainContainer}>
						<Grid item xs={12} sm={12} md={12} lg={6} sx={styles.leftContainerText}>
							<Box sx={styles.airControlContainer}>
								<Box>
									<Box component={'img'} src={AirControl} alt='' sx={styles.airControlLogo} />

									<Typography sx={styles.authCopy}>
										{t('Almost done! Before you can sign in,')}
										<br />
										{t('your new account needs to be verified')}.
									</Typography>
								</Box>
								<Box>
									<Typography sx={styles.authCopy}>
										{t('Need a new verification code?')}{' '}
										{showResendCodeButton && (
											<Box component={'span'} sx={styles.actionLink} onClick={handleRequestResend}>
												{t('Resend code!')}
											</Box>
										)}
									</Typography>
									{showResendCodeLoader && (
										<>
											<Typography sx={styles.authCopy}>Resending code</Typography>
											<LinearProgress />
										</>
									)}
									{showResendCodeEmailInput && (
										<ResendCodeEmailInput
											setShowResendCodeLoader={setShowResendCodeLoader}
											showResendCodeLoader={showResendCodeLoader}
										/>
									)}
								</Box>
							</Box>
						</Grid>
						<Grid item xs={12} sm={12} md={12} lg={6} sx={styles.rightContainer}>
							<Box component={'form'} onSubmit={handleSubmit(handleConfirmSignUp)}>
								<Box elevation={0} sx={styles.outlineContainer}>
									<Box sx={styles.formTitle}>
										{t('Verify Account')}
										{showLoader && <LinearProgress />}
									</Box>
									{!queryParameters.email && (
										<>
											<Controller
												render={({ field }) => (
													<TextField
														{...field}
														placeholder={t('Email')}
														variant='outlined'
														disabled={showLoader}
														sx={styles.textFieldNoMargin}
														autoFocus
													/>
												)}
												name='email'
												control={control}
												rules={{
													required: true,
													validate: {
														emailAddress: validateEmailRegex
													}
												}}
											/>
											<ErrorMessage name='email' error={errors.email} sx={styles.error} />
										</>
									)}

									<Controller
										render={({ field }) => (
											<TextField
												{...field}
												placeholder={t('Code')}
												variant='outlined'
												disabled={showLoader}
												sx={queryParameters.email ? styles.textFieldNoMargin : styles.textField}
												autoFocus
											/>
										)}
										name='code'
										control={control}
										rules={{
											required: true,
											validate: {
												sixDigitCode: validateSixDigitCode
											}
										}}
									/>
									<ErrorMessage name='code' error={errors.code} sx={styles.error} />
									<Button
										color='primary'
										sx={styles.button}
										variant='contained'
										id='signUp'
										type='submit'
										disabled={showLoader || hasErrors}
									>
										{t('Submit')}
									</Button>
								</Box>
								<Box sx={styles.belowSignIn}>
									<Typography sx={{ textAlign: 'center' }}>
										{t('If you already have an account you can')}{' '}
										<Box component={'span'} sx={styles.actionLink} onClick={handleClickLogin}>
											{t('Sign in here!')}
										</Box>
									</Typography>
									<Link
										href='https://www.aloft.ai/support/'
										target='_blank'
										rel='noopener noreferrer'
										underline='none'
										sx={styles.bottomSupportButton}
									>
										<Button sx={styles.outlineButton} variant='outlined' fullWidth>
											{t('Support')}
										</Button>
									</Link>
								</Box>
							</Box>
						</Grid>
					</Grid>
					<Suspense fallback={null}>
						<AuthenticationFooter />
					</Suspense>
				</Box>
			</LanguageSwitcherWrap>
		</Suspense>
	);
};

export default ConfirmSignUp;
